import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { primaryText, greyTextColor, borderPrimary, overlayTopLayer } from "../../../../../styling/stylingHelper";
import { useSubrecipeReviewStore } from "../../../../../stores/appSubrecipeReviewStore";
import { useSubrecipesStore } from "../../../../../stores/appSubrecipesStore";
import { useAppUserDefaultStore, isAllRestaurantsSelected } from "../../../../../stores/appUserDefaultStore";
import { useRestaurantStore } from "../../../../../stores/appRestaurantStore";
import { useLoadCostPrice, useLoadOrderHistory, useLoadSubrecipes } from "../../../../../utils/dataLoadingUtils";
import { resolveWastePercentage } from "../../../../../utils/wasteUtils";
import { SubrecipeReviewItem } from "../../../../../types/reviewTypes";
import SubrecipeDetailModal from "../../new/CostPrice/SubrecipeDetailModal";
import ReviewShell, { ReviewEmptyState, ReviewShellItem, ReviewShellLabels } from "../ReviewShell";
import { subrecipeNeedsAttention } from "../reviewUtils";
import SubrecipeReviewCard from "./SubrecipeReviewCard";

/**
 * Full-page review of a subrecipe template import: one subrecipe at a time, the
 * whole batch as a jump-anywhere sidebar, and the regular subrecipe editor as a
 * slide-over for anything the quick actions can't fix. Progress lives in the
 * review store (+ sessionStorage), so leaving and coming back — or a refresh —
 * resumes exactly where the user left off.
 *
 * The frame is ReviewShell, shared with the cost-price automation review.
 */
const SubrecipeReviewPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    useLoadOrderHistory();
    useLoadSubrecipes();
    // Custom ingredients (the swap panel's fallback pool) ride on the cost price load.
    useLoadCostPrice();

    const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
    const restaurantId = selectedRestaurant?.id;
    const isAllRestaurants = !!selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);

    const reviewReport = useSubrecipeReviewStore(s => s.reviewReport);
    const skippedExisting = useSubrecipeReviewStore(s => s.skippedExisting);
    const currentIndex = useSubrecipeReviewStore(s => s.currentIndex);
    const statuses = useSubrecipeReviewStore(s => s.statuses);
    const goToSubrecipe = useSubrecipeReviewStore(s => s.goToSubrecipe);
    const setSubrecipeStatus = useSubrecipeReviewStore(s => s.setSubrecipeStatus);
    const clear = useSubrecipeReviewStore(s => s.clear);
    const hydrateForRestaurant = useSubrecipeReviewStore(s => s.hydrateForRestaurant);

    const subrecipesById = useSubrecipesStore(s => s.subrecipesById);

    const [detailSubrecipeId, setDetailSubrecipeId] = useState<string | null>(null);

    // Direct navigation / refresh: pull the persisted review back out of sessionStorage.
    useEffect(() => {
        if (restaurantId && !isAllRestaurants) {
            hydrateForRestaurant(restaurantId);
        }
    }, [restaurantId, isAllRestaurants, hydrateForRestaurant]);

    const reportSettingsMap = useRestaurantStore(s => s.reportSettings);
    const reportSettings = (!restaurantId || isAllRestaurants) ? undefined : reportSettingsMap[restaurantId];
    const defaultWastePercentage = useMemo(() => resolveWastePercentage(reportSettings?.wastePercentage), [reportSettings]);

    const subrecipeItems: SubrecipeReviewItem[] = useMemo(
        () => (reviewReport && reviewReport.restaurantId === restaurantId ? reviewReport.subrecipes : []),
        [reviewReport, restaurantId]
    );

    const itemById = useMemo(() => {
        const map = new Map<string, SubrecipeReviewItem>();
        subrecipeItems.forEach(item => map.set(item.subrecipeId, item));
        return map;
    }, [subrecipeItems]);

    const items: ReviewShellItem[] = useMemo(
        () => subrecipeItems.map(item => ({
            id: item.subrecipeId,
            primaryLabel: item.name,
            secondaryLabel: t('costPrice.subrecipeReview.matchedOf', {
                matched: item.ingredientsWritten,
                total: item.ingredients.length,
            }),
        })),
        [subrecipeItems, t]
    );

    const labels: ReviewShellLabels = useMemo(() => ({
        title: t('costPrice.subrecipeReview.title'),
        back: t('costPrice.subrecipeReview.backToSubrecipes'),
        listTitle: (count) => t('costPrice.subrecipeReview.listTitle', { count }),
        progress: (current, total) => t('costPrice.subrecipeReview.progress', { current, total }),
        progressLong: (done, total, percent) => t('costPrice.subrecipeReview.progressLong', { done, total, percent }),
        acceptedPill: (count) => t('costPrice.subrecipeReview.acceptedPill', { count }),
        flaggedPill: (count) => t('costPrice.subrecipeReview.flaggedPill', { count }),
        prev: t('costPrice.subrecipeReview.prev'),
        next: t('costPrice.subrecipeReview.next'),
        shortcutHint: t('costPrice.subrecipeReview.shortcutHint'),
        flagForLater: t('costPrice.subrecipeReview.flagForLater'),
        looksGood: t('costPrice.subrecipeReview.looksGood'),
        summaryTitle: t('costPrice.subrecipeReview.summaryTitle'),
        summarySubtitle: (done, total) => t('costPrice.subrecipeReview.summarySubtitle', { done, total }),
        summaryAccepted: t('costPrice.subrecipeReview.summaryAccepted'),
        summaryFlagged: t('costPrice.subrecipeReview.summaryFlagged'),
        summaryRemaining: t('costPrice.subrecipeReview.summaryRemaining'),
        summaryFlaggedListTitle: t('costPrice.subrecipeReview.summaryFlaggedListTitle'),
        reviewAgain: t('costPrice.subrecipeReview.reviewAgain'),
        finishReview: t('costPrice.subrecipeReview.finishReview'),
    }), [t]);

    const finishReview = () => {
        clear();
        navigate('/costprice/manage-ingredients');
    };

    if (!reviewReport || reviewReport.restaurantId !== restaurantId || subrecipeItems.length === 0) {
        return (
            <ReviewEmptyState
                message={t('costPrice.subrecipeReview.noActiveReview')}
                actionLabel={t('costPrice.subrecipeReview.backToSubrecipes')}
                onAction={() => navigate('/costprice/manage-ingredients')}
            />
        );
    }

    return (
        <>
            <ReviewShell
                items={items}
                statuses={statuses}
                currentIndex={currentIndex}
                labels={labels}
                needsAttention={(itemId) => {
                    const item = itemById.get(itemId);
                    return !!item && subrecipeNeedsAttention(item);
                }}
                onGoTo={goToSubrecipe}
                onSetStatus={setSubrecipeStatus}
                onExit={() => navigate('/costprice/manage-ingredients')}
                onFinish={finishReview}
                shortcutsDisabled={!!detailSubrecipeId}
                renderCard={(itemId) => {
                    const item = itemById.get(itemId);
                    if (!item) return null;
                    return (
                        <SubrecipeReviewCard
                            key={item.subrecipeId}
                            item={item}
                            liveSubrecipe={subrecipesById.get(item.subrecipeId)}
                            defaultWastePercentage={defaultWastePercentage}
                            onOpenEditor={() => setDetailSubrecipeId(item.subrecipeId)}
                        />
                    );
                }}
                summaryExtra={skippedExisting.length > 0 ? (
                    /* Names the import refused to touch because a subrecipe with that
                       name already existed — nothing was written for them, so they are
                       listed rather than reviewable. */
                    <div>
                        <p className={`text-[14px] font-semibold ${primaryText} mb-2`}>
                            {t('costPrice.subrecipeReview.skippedTitle', { count: skippedExisting.length })}
                        </p>
                        <div className={`rounded-lg border ${borderPrimary} bg-backgroundSecondary dark:bg-backgroundSecondary-dark px-3 py-2`}>
                            <p className={`text-[13px] ${greyTextColor}`}>{skippedExisting.join(', ')}</p>
                            <p className={`text-[12px] ${greyTextColor} mt-1`}>
                                {t('costPrice.subrecipeReview.skippedHint')}
                            </p>
                        </div>
                    </div>
                ) : undefined}
            />

            {/* Full subrecipe editor for anything the quick actions can't fix */}
            {detailSubrecipeId && (
                <SubrecipeDetailModal
                    key={detailSubrecipeId}
                    subrecipeId={detailSubrecipeId}
                    onClose={() => setDetailSubrecipeId(null)}
                    // The editor portals into the root stacking context, where its
                    // default z-30 only clears the review's own z-30 action bar by
                    // DOM order. Pin it to the top layer instead of relying on that.
                    zIndexClass={overlayTopLayer}
                />
            )}
        </>
    );
};

export default SubrecipeReviewPage;
